import { Component } from '@theme/component';

const DRAG_THRESHOLD = 8;
const MEDIA_TRANSITION_MS = 800;

/**
 * @typedef {Object} StoreHeroRefs
 * @property {HTMLElement} mediaLayer
 * @property {HTMLElement} scroller
 */

/**
 * Full-screen store locator hero with exclusive accordion, drag-to-scroll, and image dissolve.
 *
 * @extends {Component<StoreHeroRefs>}
 */
class StoreHeroComponent extends Component {
  requiredRefs = ['mediaLayer', 'scroller'];

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #suppressClick = false;

  /** @type {number} */
  #pointerId = 0;

  /** @type {number} */
  #dragStartY = 0;

  /** @type {number} */
  #dragStartScroll = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #leaveTimer = 0;

  /** @type {IntersectionObserver | null} */
  #fadeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scroller = this.refs.scroller;

    this.#hoistMedia();

    scroller.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('pointermove', this.#onPointerMove, { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', this.#onPointerUp, { signal });
    scroller.addEventListener('click', this.#onScrollerClick, { capture: true, signal });
    scroller.addEventListener('scroll', this.#updateScrollFade, { signal, passive: true });
    scroller.addEventListener('wheel', this.#updateScrollFade, { signal, passive: true });

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    window.addEventListener('hashchange', this.#onHashChange, { signal });

    const hashedStore = this.#storeFromHash();
    this.#setActive(hashedStore ?? this.#firstStore(), false);

    if (hashedStore) {
      hashedStore.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }

    this.#observeScrollFade();
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#fadeObserver?.disconnect();
    this.#fadeObserver = null;
    this.#pointerId = 0;
    this.#isDragging = false;
    window.clearTimeout(this.#leaveTimer);
  }

  /**
   * @param {Event} event
   */
  activateStore(event) {
    if (this.#suppressClick) {
      event.preventDefault();
      return;
    }

    const store = this.#storeFromEvent(event);
    if (!store) return;

    this.#setActive(store);
  }

  #hoistMedia() {
    const stores = this.querySelectorAll('[data-store-hero-store]');

    for (const store of stores) {
      if (!(store instanceof HTMLElement)) continue;

      const media = store.querySelector('[data-store-hero-media]');
      if (!(media instanceof HTMLElement)) continue;

      media.dataset.storeId = store.dataset.storeHeroStore || '';
      this.refs.mediaLayer.append(media);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('a')) return;

    this.#pointerId = event.pointerId;
    this.#dragStartY = event.clientY;
    this.#dragStartScroll = this.refs.scroller.scrollTop;
    this.#isDragging = false;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (this.#pointerId === 0 || event.pointerId !== this.#pointerId) return;

    const dy = event.clientY - this.#dragStartY;

    if (!this.#isDragging && Math.abs(dy) > DRAG_THRESHOLD) {
      this.#isDragging = true;
      this.classList.add('is-dragging');
      this.refs.scroller.setPointerCapture(event.pointerId);
    }

    if (!this.#isDragging) return;

    event.preventDefault();
    this.refs.scroller.scrollTop = this.#dragStartScroll - dy;
    this.#updateScrollFade();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (this.#pointerId === 0 || event.pointerId !== this.#pointerId) return;

    if (this.#isDragging) {
      this.#suppressClick = true;
      window.setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
    }

    if (this.refs.scroller.hasPointerCapture(event.pointerId)) {
      this.refs.scroller.releasePointerCapture(event.pointerId);
    }

    this.#isDragging = false;
    this.#pointerId = 0;
    this.classList.remove('is-dragging');
  };

  /**
   * @param {MouseEvent} event
   */
  #onScrollerClick = (event) => {
    if (!this.#suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.#suppressClick = false;
  };

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.contains(event.target)) return;

    const store = event.target.closest('[data-store-hero-store]');
    if (!(store instanceof HTMLElement)) return;

    this.#setActive(store, false);
  };

  /**
   * @returns {HTMLElement | null}
   */
  #storeFromHash() {
    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) return null;

    let hash = raw;

    try {
      hash = decodeURIComponent(raw);
    } catch {
      hash = raw;
    }

    hash = hash.trim().toLowerCase();
    if (!hash) return null;

    const stores = this.querySelectorAll('[data-store-hero-store]');

    for (const store of stores) {
      if (!(store instanceof HTMLElement)) continue;

      const anchor = store.dataset.storeHeroAnchor?.toLowerCase();
      if (anchor === hash) return store;

      if (store.id && store.id.toLowerCase() === hash) return store;
    }

    return null;
  }

  /**
   * Opens the store whose Store ID matches the URL hash.
   */
  #onHashChange = () => {
    const store = this.#storeFromHash();
    if (!store) return;

    this.#setActive(store);
  };

  /**
   * @param {HTMLElement | null} store
   * @param {boolean} [smooth=true]
   */
  #setActive(store, smooth = true) {
    if (!store) return;

    const stores = this.querySelectorAll('[data-store-hero-store]');
    const previous = this.querySelector('[data-store-hero-store].is-active');

    if (previous === store) return;

    window.clearTimeout(this.#leaveTimer);

    const previousMedia = this.#mediaForStore(previous);
    const nextMedia = this.#mediaForStore(store);

    for (const item of stores) {
      if (!(item instanceof HTMLElement)) continue;

      const isActive = item === store;
      const toggle = item.querySelector('.store-hero-store__toggle');

      item.classList.toggle('is-active', isActive);

      if (toggle instanceof HTMLElement) {
        toggle.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }
    }

    for (const media of this.refs.mediaLayer.querySelectorAll('[data-store-hero-media]')) {
      media.classList.remove('is-active', 'is-leaving');
    }

    if (previous instanceof HTMLElement && previousMedia) {
      previousMedia.classList.add('is-leaving');
      this.#leaveTimer = window.setTimeout(() => {
        previousMedia.classList.remove('is-leaving');
      }, MEDIA_TRANSITION_MS);
    }

    nextMedia?.classList.add('is-active');

    if (!smooth) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    store.scrollIntoView({
      block: 'nearest',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#updateScrollFade();
  }

  /**
   * Fades the list at the top only when the scroller can move up.
   */
  #updateScrollFade = () => {
    const scroller = this.refs.scroller;
    if (!(scroller instanceof HTMLElement)) return;

    scroller.classList.toggle('store-hero-list__scroll--can-scroll-up', scroller.scrollTop > 0);
  };

  #observeScrollFade() {
    const scroller = this.refs.scroller;
    const sentinel = scroller.querySelector('[data-store-hero-scroll-top]');

    this.#updateScrollFade();

    if (!(sentinel instanceof HTMLElement)) return;

    this.#fadeObserver?.disconnect();
    this.#fadeObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const canScrollUp = Boolean(entry) && !entry.isIntersecting;
        scroller.classList.toggle('store-hero-list__scroll--can-scroll-up', canScrollUp);
      },
      { root: scroller, threshold: 0 }
    );
    this.#fadeObserver.observe(sentinel);
  }

  /**
   * @param {Element | null} store
   * @returns {HTMLElement | null}
   */
  #mediaForStore(store) {
    if (!(store instanceof HTMLElement)) return null;

    const storeId = store.dataset.storeHeroStore;
    if (!storeId) return null;

    const media = this.refs.mediaLayer.querySelector(`[data-store-id="${CSS.escape(storeId)}"]`);
    return media instanceof HTMLElement ? media : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #firstStore() {
    const store = this.querySelector('[data-store-hero-store]');
    return store instanceof HTMLElement ? store : null;
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #storeFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const store = target.closest('[data-store-hero-store]');
    return store instanceof HTMLElement ? store : null;
  }
}

if (!customElements.get('store-hero-component')) {
  customElements.define('store-hero-component', StoreHeroComponent);
}
